import React from 'react';
import ReactDOM from 'react-dom';
import loadable from '@loadable/component';
import { getSpaContainerId } from './Utils/Spectate';
import { getUrlContext, navigateTo } from './Utils/NavigationAdapter';
import { getWsInterface } from './Utils/Websockets';
import {
    setSpectateClientModeProxy,
    setSpectateClientModeUi,
    onSpectateClientModeChange,
    isProxyMode,
} from './Utils/SpectateClientMode';
import {
    addToBetslipFromDeeplink,
} from '~Betslip/interface/interactions';
import { changeBrowserURI } from './Utils/WrapperIntegration/Ucf';
import { deeplinkRedirect } from '~shared/services/deeplink';
import isEmpty from 'lodash/isEmpty';
import { statscenterInteraction } from './Utils/AsyncStatsCenterUtilsProxy';

const Spa = loadable(
    () => import(/* webpackChunkName: "spa" */ './Spa')
);

export default class SpaManager {
    constructor() {
        this.context = null;
        this.isRendered = false;
        this.spaNode = document.getElementById(getSpaContainerId());
        setSpectateClientModeProxy();

        onSpectateClientModeChange(() => {
            if (isProxyMode()) {
                getWsInterface().closeAllConnections();
            }
        });
    }

    splitUrlToPathAndParams(url) {
        return url.split('?');
    }

    isDeeplinkUrl(url) {
        return url.includes('deeplink');
    }

    async preload(initialUrlPath) {
        Spa.preload();

        /**
         * Do not get context in case of Deeplink URL
         * because there is an additional logic which may alter the url
         * for which we want to get the context for.
         *
         * Also, Deeplinks are interacting with Message Broker and we need to avoid
         * this interaction on preload.
         */
        if (!this.isDeeplinkUrl(initialUrlPath)) {
            const [ urlWithoutParams ] = this.splitUrlToPathAndParams(initialUrlPath);
            this.context = await getUrlContext(urlWithoutParams);
        }
    }

    destroy() {
        ReactDOM.unmountComponentAtNode(this.spaNode);
        this.isRendered = false;
        this.context = null;
        setSpectateClientModeProxy();
    }

    async render(initialUrlPath) {
        let [
            urlWithoutParams,
            queryStringParams,
        ] = this.splitUrlToPathAndParams(initialUrlPath);
        const urlParams = new URLSearchParams(queryStringParams);
        let openStats = urlParams.get('open_stats');
        if (openStats !== null) {
            openStats = JSON.parse(openStats);
            if (openStats === 1) {
                statscenterInteraction('openStatsCenterPopup');
                urlWithoutParams = '/';
                changeBrowserURI(urlWithoutParams);
            }
        }

        if (this.isDeeplinkUrl(urlWithoutParams)) {
            let selectionIds = urlParams.get('selections');

            if (selectionIds !== null) {
                selectionIds = JSON.parse(selectionIds);
                if (!isEmpty(selectionIds)) {
                    const surveyId = urlParams.get('surveyId');
                    addToBetslipFromDeeplink(selectionIds, surveyId);

                    await deeplinkRedirect(selectionIds)
                        .then(({ url }) => {
                            urlWithoutParams = url;
                            changeBrowserURI(urlWithoutParams);
                        });
                } else {
                    urlWithoutParams = '/';
                    changeBrowserURI(urlWithoutParams);
                }
            }
        }

        if (this.isRendered) {
            navigateTo(urlWithoutParams);

            return;
        }

        const context = this.context || await getUrlContext(urlWithoutParams);

        ReactDOM.render(
            <Spa initialContext={context} />,
            this.spaNode,
            () => {
                this.isRendered = true;
                setSpectateClientModeUi();
            }
        );
    }
}
